import React from 'react';
import LegalPageShell from '../components/Legal/LegalPageShell';

const PrivacyPolicy: React.FC = () => {
  return (
    <LegalPageShell title="Privacy Policy">
      <p className="mb-3">
        <strong className="text-dark">Effective date:</strong> 04 April 2026
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">1. Introduction</h2>
      <p>
        This Privacy Policy explains how Karais Limited (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) collects,
        uses, stores, shares, and protects your personal information when you access or use the Karen Ruimy mobile
        application (the &quot;App&quot;), our websites, products, services, events, content, and related communications.
      </p>
      <p>
        The Karen Ruimy App is a personal growth, wellness, and transformational platform providing access to guided
        meditations, transformational journeys, journaling tools, oracle guidance, educational content, AI-powered
        guidance, workshops, webinars, and other wellbeing resources created by Karen Ruimy.
      </p>
      <p>
        By downloading, accessing, creating an account for, or using the App, you agree to the collection and use of
        information in accordance with this Privacy Policy.
      </p>
      <p>Karais Limited is the data controller responsible for your personal information.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">2. Information We Collect</h2>

      <h3 className="h6 fw-semibold text-dark mt-3 mb-2">Information You Provide</h3>
      <p>We may collect information that you voluntarily provide, including:</p>
      <ul>
        <li>Full name</li>
        <li>Email address</li>
        <li>Telephone number</li>
        <li>Billing address</li>
        <li>Account login credentials</li>
        <li>Subscription preferences</li>
        <li>Journal entries and reflections</li>
        <li>Responses to questionnaires and assessments</li>
        <li>Communications with our support team</li>
        <li>Event registrations</li>
        <li>Marketing preferences</li>
      </ul>

      <h3 className="h6 fw-semibold text-dark mt-3 mb-2">Subscription and Payment Information</h3>
      <p>
        When you purchase a subscription or product through the App, payment information is processed securely through
        third-party payment providers including Apple App Store, Google Play Store, Stripe, PayPal, or other authorised
        providers.
      </p>
      <p>We do not store full payment card details on our servers.</p>

      <h3 className="h6 fw-semibold text-dark mt-3 mb-2">Ask Karen AI Interactions</h3>
      <p>When you use Ask Karen AI, we may collect:</p>
      <ul>
        <li>Questions submitted</li>
        <li>Prompts entered</li>
        <li>Responses generated</li>
        <li>Usage history</li>
        <li>Feature interaction data</li>
      </ul>
      <p>These interactions may be processed by trusted third-party AI service providers.</p>

      <h3 className="h6 fw-semibold text-dark mt-3 mb-2">Journal and Reflection Data</h3>
      <p>If you use journaling or reflection features, we may store:</p>
      <ul>
        <li>Journal entries</li>
        <li>Personal reflections</li>
        <li>Milestones</li>
        <li>Progress tracking information</li>
        <li>Saved goals and intentions</li>
      </ul>
      <p>
        This information is associated with your account and stored securely to enable access across devices.
      </p>

      <h3 className="h6 fw-semibold text-dark mt-3 mb-2">Information Collected Automatically</h3>
      <p>When you use the App, we may automatically collect:</p>
      <ul>
        <li>Device type</li>
        <li>Operating system</li>
        <li>Device identifiers</li>
        <li>IP address</li>
        <li>Time zone</li>
        <li>App version</li>
        <li>Crash reports</li>
        <li>Diagnostic information</li>
        <li>Usage patterns</li>
        <li>Pages and content viewed</li>
        <li>Session activity</li>
      </ul>

      <h3 className="h6 fw-semibold text-dark mt-3 mb-2">Analytics Information</h3>
      <p>We may collect analytics information to improve performance and user experience, including:</p>
      <ul>
        <li>Feature usage</li>
        <li>Session duration</li>
        <li>Navigation patterns</li>
        <li>Engagement metrics</li>
        <li>Conversion data</li>
      </ul>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">3. How We Use Your Information</h2>
      <p>We may use your information to:</p>
      <ul>
        <li>Create and manage your account</li>
        <li>Provide access to App features</li>
        <li>Deliver meditations, journeys, workshops, and content</li>
        <li>Process subscriptions and payments</li>
        <li>Personalise content recommendations</li>
        <li>Provide customer support</li>
        <li>Improve our services</li>
        <li>Monitor App performance</li>
        <li>Prevent fraud and misuse</li>
        <li>Send important service notifications</li>
        <li>Deliver newsletters and marketing communications where consent has been provided</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">4. Ask Karen AI</h2>
      <p>The App may include AI-powered guidance features known as Ask Karen AI.</p>
      <p>
        Information submitted through Ask Karen AI may be processed by third-party artificial intelligence providers for
        the purpose of generating responses and improving service functionality.
      </p>
      <p>
        Ask Karen AI is intended for educational, informational, wellbeing, and personal development purposes only.
      </p>
      <p>Ask Karen AI:</p>
      <ul>
        <li>Does not provide medical advice</li>
        <li>Does not provide psychological therapy</li>
        <li>Does not provide psychiatric treatment</li>
        <li>Does not provide legal advice</li>
        <li>Does not provide financial advice</li>
        <li>Should not be relied upon as a substitute for professional services</li>
      </ul>
      <p>Users should avoid submitting highly sensitive personal information through AI features.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">5. Oracle Readings and Spiritual Guidance</h2>
      <p>
        The App may provide oracle card readings, spiritual guidance, manifestation tools, and related content.
      </p>
      <p>
        Such content is intended solely for personal reflection, inspiration, educational, and entertainment purposes.
      </p>
      <p>
        Oracle readings and spiritual guidance should not be relied upon for medical, legal, financial, psychological, or
        other professional decisions.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">6. How We Share Information</h2>
      <p>We do not sell your personal information.</p>
      <p>We may share information with trusted third parties including:</p>
      <ul>
        <li>Payment processors</li>
        <li>Cloud hosting providers</li>
        <li>Analytics providers</li>
        <li>Email marketing platforms</li>
        <li>Customer support providers</li>
        <li>Artificial intelligence providers</li>
        <li>Webinar and event platforms</li>
        <li>Professional advisers</li>
        <li>Regulatory authorities where required by law</li>
      </ul>
      <p>All third-party providers are required to implement appropriate safeguards for your information.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">7. International Data Transfers</h2>
      <p>
        Because the Karen Ruimy App is available globally, your information may be processed in countries outside your
        country of residence.
      </p>
      <p>
        Where personal data is transferred internationally, we implement appropriate safeguards, including:
      </p>
      <ul>
        <li>Standard Contractual Clauses</li>
        <li>Adequacy decisions</li>
        <li>Contractual protections</li>
        <li>Other lawful transfer mechanisms</li>
      </ul>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">8. Data Security</h2>
      <p>
        We implement appropriate technical and organisational measures to protect personal information, including:
      </p>
      <ul>
        <li>Encrypted transmission</li>
        <li>Secure cloud storage</li>
        <li>Access controls</li>
        <li>Authentication measures</li>
        <li>Monitoring and security reviews</li>
      </ul>
      <p>
        While we take reasonable steps to protect information, no system can be guaranteed to be completely secure.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">9. Data Retention</h2>
      <p>We retain personal information only for as long as necessary to:</p>
      <ul>
        <li>Provide services</li>
        <li>Maintain your account</li>
        <li>Comply with legal obligations</li>
        <li>Resolve disputes</li>
        <li>Enforce agreements</li>
      </ul>
      <p>When information is no longer required, it is securely deleted or anonymised.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">10. Push Notifications</h2>
      <p>The App may send notifications relating to:</p>
      <ul>
        <li>New content</li>
        <li>Subscription updates</li>
        <li>Event announcements</li>
        <li>Product updates</li>
        <li>Personal reminders</li>
        <li>Marketing communications where permitted</li>
      </ul>
      <p>You may manage notification preferences through your device settings.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">11. Marketing Communications</h2>
      <p>Where permitted by law, we may send:</p>
      <ul>
        <li>Newsletters</li>
        <li>Promotional content</li>
        <li>Event invitations</li>
        <li>Product announcements</li>
        <li>Educational content</li>
      </ul>
      <p>
        You may unsubscribe at any time using the unsubscribe link included in communications or by contacting us.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">12. Children&apos;s Privacy</h2>
      <p>The Karen Ruimy App is intended for users aged 16 years and older.</p>
      <p>We do not knowingly collect personal information from children under 16.</p>
      <p>
        If we become aware that information has been collected from a child under 16 without appropriate consent, we
        will delete such information.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">13. Your Privacy Rights</h2>
      <p>Depending on your location, you may have rights to:</p>
      <ul>
        <li>Access your information</li>
        <li>Correct inaccurate information</li>
        <li>Request deletion</li>
        <li>Restrict processing</li>
        <li>Object to processing</li>
        <li>Withdraw consent</li>
        <li>Request data portability</li>
        <li>Lodge a complaint with a supervisory authority</li>
      </ul>
      <p>To exercise your rights, contact us using the details below.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">14. Third-Party Services</h2>
      <p>The App may integrate with third-party providers including:</p>
      <ul>
        <li>Apple App Store</li>
        <li>Google Play Store</li>
        <li>Payment providers</li>
        <li>Analytics providers</li>
        <li>Artificial intelligence providers</li>
        <li>Webinar and event platforms</li>
      </ul>
      <p>
        Your use of third-party services may also be governed by their own privacy policies and terms.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">15. Cookies and Similar Technologies</h2>
      <p>Our websites and certain App functions may use cookies and similar technologies to:</p>
      <ul>
        <li>Enable functionality</li>
        <li>Improve performance</li>
        <li>Analyse usage</li>
        <li>Personalise experiences</li>
        <li>Support marketing activities</li>
      </ul>
      <p>You may control cookie preferences through your browser settings where applicable.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">16. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy from time to time.</p>
      <p>
        Updated versions will be posted within the App and on our websites with a revised effective date.
      </p>
      <p>
        Continued use of the App following updates constitutes acceptance of the revised Privacy Policy.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">17. Contact Us</h2>
      <p>
        If you have questions regarding this Privacy Policy or wish to exercise your privacy rights, please contact:
      </p>
      <p>
        <strong className="text-dark">Karais Limited</strong>
        <br />
        56 Grosvenor Street
        <br />
        London
        <br />
        W1K 3HZ
        <br />
        United Kingdom
      </p>
      <p>
        <strong className="text-dark">Email:</strong>{' '}
        <a href="mailto:team@karenruimy.com" className="text-decoration-none">
          team@karenruimy.com
        </a>
      </p>
      <p>
        <strong className="text-dark">Data Controller:</strong> Karais Limited
      </p>
      <p>
        <strong className="text-dark">Applicable Services:</strong> Karen Ruimy App, karenruimy.com, and associated
        services.
      </p>
    </LegalPageShell>
  );
};

export default PrivacyPolicy;
